const OUTBOUND_URL_DIMENSION_KEY = 'outboundurl';

function normalizeLeadField(field: string): string {
  return field.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isLeadPiiField(field: string): boolean {
  const normalized = normalizeLeadField(field);
  return (
    normalized.includes('firstname') ||
    normalized.includes('lastname') ||
    normalized.includes('email') ||
    normalized.includes('phone') ||
    normalized.includes('address')
  );
}

export function isOutboundUrlDimensionKey(key: string): boolean {
  return key.toLowerCase() === OUTBOUND_URL_DIMENSION_KEY;
}

/**
 * True when an interpolator source path is a lead PII field
 * (`lead.firstName`, `lead.email`, `lead.phoneNumber`, …). Partner query-key
 * spelling is ignored.
 */
export function isLeadPiiSourcePath(sourcePath: string): boolean {
  const [root, ...segments] = sourcePath.trim().split('.');
  if (root !== 'lead' || segments.length === 0) {
    return false;
  }
  const field = segments.join('.');
  return isLeadPiiField(field);
}

export function leadPiiMappedQueryKeys(
  fieldMap?: Record<string, string>,
): string[] {
  if (!fieldMap) {
    return [];
  }
  return Object.entries(fieldMap)
    .filter(([, sourcePath]) => isLeadPiiSourcePath(sourcePath))
    .map(([queryKey]) => queryKey);
}

function parseTelemetryUrl(url: string): URL {
  if (/^https?:\/\//i.test(url)) {
    return new URL(url);
  }
  if (url.startsWith('//')) {
    return new URL(`https:${url}`);
  }
  return new URL(url, 'https://telemetry.invalid');
}

function serializeTelemetryUrl(original: string, parsed: URL): string {
  if (/^https?:\/\//i.test(original)) {
    return parsed.toString();
  }
  if (original.startsWith('//')) {
    return `//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  const path = original.replace(/[?#].*$/, '');
  return `${path}${parsed.search}${parsed.hash}`;
}

/**
 * Drop interpolator query params whose source is lead PII before the URL
 * is sent to Tagular or New Relic. `window.open` destinations are
 * unchanged — this is the telemetry chokepoint only.
 *
 * Partner query-key names are not a signal. Non-PII lead fields, partner
 * identifiers (`SYSTUDENTID`, `sparkroomid`), and static params
 * (`SOURCEAPP`) stay. Relative and protocol-relative URLs keep their
 * original prefix. Parse failures return `undefined` (fail closed).
 */
export function sanitizeOutboundUrlForTelemetry(
  url: string | null | undefined,
  fieldMap?: Record<string, string>,
): string | null | undefined {
  if (url == null || url === '') {
    return url;
  }

  try {
    const parsed = parseTelemetryUrl(url);
    const leadPiiKeys = new Set(
      leadPiiMappedQueryKeys(fieldMap).map((key) => key.toLowerCase()),
    );

    // `forEach` rather than spreading `.keys()`: this package compiles to
    // ES5, where iterator spread is empty without downlevelIteration.
    const keysToDelete: string[] = [];
    parsed.searchParams.forEach((_value, key) => {
      if (leadPiiKeys.has(key.toLowerCase())) {
        keysToDelete.push(key);
      }
    });

    if (keysToDelete.length === 0) {
      return url;
    }

    keysToDelete.forEach((key) => {
      parsed.searchParams.delete(key);
    });

    return serializeTelemetryUrl(url, parsed);
  } catch {
    return undefined;
  }
}

export function sanitizeTelemetryAttributeValue(
  value: string | number | boolean,
): string | number | boolean | undefined {
  if (typeof value !== 'string') {
    return value;
  }
  return sanitizeOutboundUrlForTelemetry(value) ?? undefined;
}

/**
 * Interpolator consumers (`applicationCta` / `postLeadCta`) pass the
 * click href and field map here before Tagular or New Relic. Only query
 * keys mapped from lead PII are dropped. Empty / hide / unparseable
 * results stay off the event.
 */
export function ctaUrlForTelemetry(
  url: string | null | undefined,
  fieldMap?: Record<string, string>,
): string | undefined {
  if (url == null || url === '') {
    return undefined;
  }
  return sanitizeOutboundUrlForTelemetry(url, fieldMap) ?? undefined;
}
